import React from 'react';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import RaisedButton from 'material-ui/RaisedButton';
import { browserHistory } from 'react-router';
import Dialog from 'material-ui/Dialog';
import {
  UserPlus, CheckCircle, Clock, ThumbsUp,
  FileText, Hourglass, BadgeCheck, Building2,
  XCircle, Activity, BarChart2,
  PlusCircle, Eye,
  Users, MapPin, Network, Wrench,
} from './Icons';
import Header from './Header';
import { computePermissions, NAV_GROUPS } from './Sidebar';
import userdata from '../config/jwt_token';

/* ─── Module card metadata with Lucide icon components ─── */
const MODULE_META = {
  '/vendor/onboard':          { label: 'Vendor Onboarding',   Icon: UserPlus,    color: '#0891b2', desc: 'Register & submit new vendors'          },
  '/vendor/approved':         { label: 'Verified Vendors',    Icon: CheckCircle, color: '#16a34a', desc: 'Browse approved vendor records'         },
  '/vendor/pending':          { label: 'Pending Vendors',     Icon: Clock,       color: '#d97706', desc: 'Vendors awaiting verification'          },
  '/vendor/approval':         { label: 'Approve Vendors',     Icon: ThumbsUp,    color: '#7c3aed', desc: 'Review and approve pending vendors'     },
  '/initializePR':            { label: 'Payment Request',     Icon: FileText,    color: '#2563eb', desc: 'Create and initialize payment requests' },
  '/vendor/payment/pending':  { label: 'Pending Payments',   Icon: Hourglass,   color: '#d97706', desc: 'Payments awaiting processing'           },
  '/vendor/payment/approve':  { label: 'Approve Payments',   Icon: ThumbsUp,    color: '#059669', desc: 'Approve queued payment requests'        },
  '/vendor/payment/approved': { label: 'Processed Payments', Icon: BadgeCheck,  color: '#16a34a', desc: 'View completed payment records'         },
  '/payment/generated_buf':   { label: 'Generated BUF',      Icon: Building2,   color: '#0891b2', desc: 'Bank upload format files generated'     },
  '/payment/buf-rejected':    { label: 'BUF Rejected',       Icon: XCircle,     color: '#dc2626', desc: 'Payments rejected by the bank'           },
  '/payment/status':          { label: 'Payment Status',     Icon: Activity,    color: '#7c3aed', desc: 'Track real-time payment status'         },
  '/cost-center/add':         { label: 'Add Cost Center',    Icon: PlusCircle,  color: '#0891b2', desc: 'Create a new cost center entry'         },
  '/cost-center/view':        { label: 'View Cost Center',   Icon: Eye,         color: '#2563eb', desc: 'Browse existing cost centers'           },
  '/payment/reports':         { label: 'Reports Hub',        Icon: BarChart2,   color: '#7c3aed', desc: 'Financial reports and analytics'        },
  '/users':                   { label: 'Add Users',          Icon: Users,       color: '#059669', desc: 'Manage system user accounts'            },
  '/cities':                  { label: 'City Management',    Icon: MapPin,      color: '#0891b2', desc: 'Manage city configurations'             },
  '/business-units':          { label: 'BU Management',      Icon: Network,     color: '#d97706', desc: 'Configure business units'               },
  '/recoverable-amount-config':{ label: 'Recoverable Config', Icon: Wrench,     color: '#dc2626', desc: 'Set up recoverable amount rules'        },
};

/* ─── Build flat list of all modules from Sidebar nav ─── */
function buildAllModules() {
  const modules = [];
  NAV_GROUPS.forEach(group => {
    if (group.id === 'home') return;
    group.items.forEach(item => {
      modules.push({ ...item, group: group.label });
    });
  });
  return modules;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

class HomeScreen extends React.Component {
  constructor(props) {
    super(props);
    this.state = { permissions: {}, dialogOpen: false };
  }

  componentWillMount() {
    if (!userdata) { browserHistory.push('/'); return; }
    this.setState({ permissions: computePermissions() });
  }

  handleClose() { this.setState({ dialogOpen: false }); }

  handleNavigate(path, permKey) {
    const disabled = permKey ? !!this.state.permissions[permKey] : false;
    if (disabled) { this.setState({ dialogOpen: true }); return; }
    browserHistory.push(path);
  }

  render() {
    const { permissions } = this.state;
    const allModules = buildAllModules();
    const firstName  = userdata ? (userdata.name || '').split(' ')[0] : '';
    const role       = userdata ? (userdata.role_label || userdata.role || '') : '';

    /* Group modules by section */
    const grouped = {};
    allModules.forEach(m => {
      if (!grouped[m.group]) grouped[m.group] = [];
      grouped[m.group].push(m);
    });

    return (
      <div style={{ background: 'var(--bg, #f1f5f9)', minHeight: '100vh' }}>
        <MuiThemeProvider>
          <Header />

          {/* ── Dashboard content ── */}
          <div className="zaps-dash-wrap">

            {/* Greeting */}
            <div className="zaps-dash-greeting">
              <h2>
                {getGreeting()}, {firstName} 👋
                {role && <span className="zaps-role-badge">{role}</span>}
              </h2>
              <p>Here's a quick overview of your accessible modules. Click any card to navigate.</p>
            </div>

            {/* Module sections */}
            {Object.entries(grouped).map(([groupLabel, items]) => {
              const cards = items.filter(item => MODULE_META[item.path]);
              if (!cards.length) return null;

              return (
                <div key={groupLabel} style={{ marginBottom: 32 }}>
                  <div className="zaps-dash-section-title">{groupLabel}</div>
                  <div className="zaps-dash-grid">
                    {cards.map(item => {
                      const meta     = MODULE_META[item.path];
                      const disabled = item.permKey ? !!permissions[item.permKey] : false;
                      const { Icon } = meta;

                      return (
                        <div
                          key={item.path}
                          className={`zaps-module-card${disabled ? ' disabled' : ''}`}
                          onClick={() => this.handleNavigate(item.path, item.permKey)}
                          title={disabled ? "You don't have access to this section" : meta.label}
                        >
                          <div
                            className="zaps-module-card-icon"
                            style={{
                              background: `linear-gradient(135deg, ${meta.color}dd, ${meta.color}88)`,
                            }}
                          >
                            <Icon size={19} strokeWidth={1.8} color="#fff" />
                          </div>
                          <h4>{meta.label}</h4>
                          <p>{meta.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Access denied dialog ── */}
          <Dialog
            title="Access Restricted"
            actions={
              <RaisedButton
                label="OK"
                primary={true}
                onClick={this.handleClose.bind(this)}
              />
            }
            modal={true}
            contentStyle={{ width: '100%', maxWidth: '480px' }}
            open={this.state.dialogOpen}
          >
            You don't have access to this section. Please contact{' '}
            <strong>product@zolostays.com</strong> if you need access.
          </Dialog>
        </MuiThemeProvider>
      </div>
    );
  }
}

export default HomeScreen;


// WEBPACK FOOTER //
// ./src/components/HomeScreen.js